"use client"; // Error components must be Client Components

import { useEffect } from "react";
import GradientButton from "@/components/Buttons/GradientButton";

// Detect errors caused by deployment skew (old cached HTML referencing
// JS/CSS chunks that no longer exist after a new build).
function isChunkOrHydrationError(error: Error): boolean {
  const msg = error.message || "";
  const name = error.name || "";
  return (
    name === "ChunkLoadError" ||
    msg.includes("Loading chunk") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Load failed") ||
    msg.includes("CSS_CHUNK_LOAD_FAILED") ||
    msg.includes("insertBefore") ||
    msg.includes("removeChild") ||
    msg.includes("Minified React error")
  );
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Application Error:", error);

    // Auto-reload once for deployment skew / chunk load errors
    if (isChunkOrHydrationError(error)) {
      const key = "route-error-reload";
      const lastReload = sessionStorage.getItem(key);
      const now = Date.now();

      // Allow one auto-reload per 60 seconds
      if (!lastReload || now - parseInt(lastReload, 10) > 60_000) {
        sessionStorage.setItem(key, now.toString());
        window.location.reload();
        return;
      }
    }
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center space-y-6">
      <h2 className="text-3xl font-bold text-gray-800">
        Something went wrong!
      </h2>
      <p className="text-gray-600 max-w-md">
        We apologize for the inconvenience. An unexpected error has occurred.
        {error.digest && (
          <span className="block mt-2 text-xs text-gray-400">
            Error ID: {error.digest}
          </span>
        )}
      </p>
      <div className="flex gap-4">
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
        >
          Reload Page
        </button>
        <GradientButton href="/" text="Go Home" textSize="text-base" />
      </div>
    </div>
  );
}
